import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function CustomerFinanceTab({ customerId }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    company: true,
    account: true,
    invoicing: false,
    contacts: false,
    cis: false,
    pricing: false,
    balance: true,
    xero: false
  });

  useEffect(() => {
    fetchCustomerFinance();
  }, [customerId]);

  const fetchCustomerFinance = async () => {
    try {
      const res = await apiFetch(`/api/customers/${customerId}`);
      const data = await res.json();
      setCustomer(data);
      setEditForm(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching customer finance:', err);
      setLoading(false);
    }
  };

  const handleSave = async (section) => {
    try {
      const res = await apiFetch(`/api/customers/${customerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setCustomer(data);
        setEditingSection(null);
        alert('Saved successfully!');
      }
    } catch (err) {
      console.error('Error saving:', err);
      alert('Failed to save');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!customer) {
    return <div className="p-6 text-center text-gray-500">Customer not found</div>;
  }

  return (
    <div className="p-6 space-y-4 bg-gray-50">
      {/* Header with Status Badges */}
      <div className="flex justify-between items-center bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Finance & Accounting</h2>
          <p className="text-sm text-gray-600 mt-1">Account management and financial details</p>
        </div>
        <div className="flex items-center space-x-2">
          {customer.account_on_stop && (
            <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium border border-red-300">
              🚫 Account On Stop
            </span>
          )}
          <span className={`px-3 py-1 rounded-full text-sm font-medium border ${
            customer.credit_status === 'approved' ? 'bg-green-100 text-green-800 border-green-300' :
            customer.credit_status === 'on_stop' ? 'bg-red-100 text-red-800 border-red-300' :
            'bg-yellow-100 text-yellow-800 border-yellow-300'
          }`}>
            {customer.credit_status === 'approved' ? '✅ Approved' : 
             customer.credit_status === 'on_stop' ? '🚫 On Stop' : 
             '⏳ Pending'}
          </span>
        </div>
      </div>

      {/* Section 1: Company & Legal Information */}
      <CollapsibleSection
        title="Company & Legal Information"
        icon="🏢"
        color="blue"
        expanded={expandedSections.company}
        onToggle={() => toggleSection('company')}
      >
        <CompanyLegalSection
          customer={customer}
          editForm={editForm}
          setEditForm={setEditForm}
          editing={editingSection === 'company'}
          onEdit={() => setEditingSection('company')}
          onSave={() => handleSave('company')}
          onCancel={() => {
            setEditingSection(null);
            setEditForm(customer);
          }}
        />
      </CollapsibleSection>

      {/* Section 2: Account Settings (Always expanded - most used) */}
      <div className="bg-white rounded-lg shadow-sm border-l-4 border-green-500">
        <div className="p-5">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <span className="text-2xl">💳</span>
              <h3 className="text-lg font-semibold text-gray-800">Account Settings</h3>
            </div>
            {editingSection !== 'account' ? (
              <button 
                onClick={() => setEditingSection('account')}
                className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium"
              >
                Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button 
                  onClick={() => handleSave('account')}
                  className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded font-medium"
                >
                  Save
                </button>
                <button 
                  onClick={() => {
                    setEditingSection(null);
                    setEditForm(customer);
                  }}
                  className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <AccountSettingsSection
            customer={customer}
            editForm={editForm}
            setEditForm={setEditForm}
            editing={editingSection === 'account'}
          />
        </div>
      </div>

      {/* Section 3: Invoicing & Payment Details */}
      <CollapsibleSection
        title="Invoicing & Payment Details"
        icon="📄"
        color="purple"
        expanded={expandedSections.invoicing}
        onToggle={() => toggleSection('invoicing')}
      >
        <InvoicingSection
          customer={customer}
          editForm={editForm}
          setEditForm={setEditForm}
          editing={editingSection === 'invoicing'}
          onEdit={() => setEditingSection('invoicing')}
          onSave={() => handleSave('invoicing')}
          onCancel={() => {
            setEditingSection(null);
            setEditForm(customer);
          }}
        />
      </CollapsibleSection>

      {/* Section 4: Contact Details */}
      <CollapsibleSection
        title="Contact Details"
        icon="📞"
        color="indigo"
        expanded={expandedSections.contacts}
        onToggle={() => toggleSection('contacts')}
      >
        <ContactsSection
          customer={customer}
          editForm={editForm}
          setEditForm={setEditForm}
          editing={editingSection === 'contacts'}
          onEdit={() => setEditingSection('contacts')}
          onSave={() => handleSave('contacts')}
          onCancel={() => {
            setEditingSection(null);
            setEditForm(customer);
          }}
        />
      </CollapsibleSection>

      {/* Section 5: CIS Details (Conditional) */}
      <CollapsibleSection
        title="CIS Details (Construction Industry Scheme)"
        icon="🔨"
        color="orange"
        expanded={expandedSections.cis}
        onToggle={() => toggleSection('cis')}
        badge={customer.is_cis_registered ? 'Active' : null}
      >
        <CISSection
          customer={customer}
          editForm={editForm}
          setEditForm={setEditForm}
          editing={editingSection === 'cis'}
          onEdit={() => setEditingSection('cis')}
          onSave={() => handleSave('cis')}
          onCancel={() => {
            setEditingSection(null);
            setEditForm(customer);
          }}
        />
      </CollapsibleSection>

      {/* Section 6: Pricing & Discounts */}
      <CollapsibleSection
        title="Pricing & Discounts"
        icon="💰"
        color="yellow"
        expanded={expandedSections.pricing}
        onToggle={() => toggleSection('pricing')}
      >
        <PricingSection
          customer={customer}
          editForm={editForm}
          setEditForm={setEditForm}
          editing={editingSection === 'pricing'}
          onEdit={() => setEditingSection('pricing')}
          onSave={() => handleSave('pricing')}
          onCancel={() => {
            setEditingSection(null);
            setEditForm(customer);
          }}
        />
      </CollapsibleSection>

      {/* Section 7: Current Balance (Always expanded - important) */}
      <div className="bg-white rounded-lg shadow-sm border-l-4 border-green-500">
        <div className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">💷</span>
            <h3 className="text-lg font-semibold text-gray-800">Current Balance</h3>
          </div>
          <CurrentBalanceSection customer={customer} />
        </div>
      </div>

      {/* Section 8: Xero Integration */}
      <CollapsibleSection
        title="Xero Integration"
        icon="📊"
        color="teal"
        expanded={expandedSections.xero}
        onToggle={() => toggleSection('xero')}
      >
        <XeroSection customer={customer} />
      </CollapsibleSection>
    </div>
  );
}

// Reusable Collapsible Section Component
function CollapsibleSection({ title, icon, color, expanded, onToggle, children, badge }) {
  const colorClasses = {
    blue: 'border-blue-500 bg-blue-50',
    green: 'border-green-500 bg-green-50',
    purple: 'border-purple-500 bg-purple-50',
    indigo: 'border-indigo-500 bg-indigo-50',
    orange: 'border-orange-500 bg-orange-50',
    yellow: 'border-yellow-500 bg-yellow-50',
    teal: 'border-teal-500 bg-teal-50',
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border-l-4 ${colorClasses[color] || colorClasses.blue}`}>
      <button
        onClick={onToggle}
        className="w-full p-4 flex justify-between items-center hover:bg-gray-50 transition"
      >
        <div className="flex items-center gap-2">
          <span className="text-2xl">{icon}</span>
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          {badge && (
            <span className={`ml-2 px-2 py-0.5 bg-${color}-100 text-${color}-800 rounded-full text-xs font-medium`}>
              {badge}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'transform rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="p-5 pt-0 border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
}

// Section Components
function CompanyLegalSection({ customer, editForm, setEditForm, editing, onEdit, onSave, onCancel }) {
  if (!editing) {
    return (
      <div>
        <div className="flex justify-end mb-3">
          <button onClick={onEdit} className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium">
            Edit
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoField label="Organisation Type" value={customer.organisation_type} />
          <InfoField label="Company Reg No" value={customer.company_reg_no} />
          <InfoField label="VAT Number" value={customer.vat_number} />
          <div className="md:col-span-2">
            <InfoField 
              label="Registered Address" 
              value={formatAddress(
                customer.registered_address_line1,
                customer.registered_address_line2,
                customer.registered_city,
                customer.registered_county,
                customer.registered_postcode,
                customer.registered_country
              )}
              multiline
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-3 gap-2">
        <button onClick={onSave} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded font-medium">
          Save
        </button>
        <button onClick={onCancel} className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium">
          Cancel
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Organisation Type</label>
          <select
            value={editForm.organisation_type || ''}
            onChange={(e) => setEditForm({...editForm, organisation_type: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Select type...</option>
            <option value="ltd">Limited Company</option>
            <option value="plc">Public Limited Company</option>
            <option value="llp">Limited Liability Partnership</option>
            <option value="partnership">Partnership</option>
            <option value="sole_trader">Sole Trader</option>
            <option value="trust">Trust</option>
            <option value="charity">Charity</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Company Reg No</label>
          <input
            type="text"
            value={editForm.company_reg_no || ''}
            onChange={(e) => setEditForm({...editForm, company_reg_no: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="12345678 or SC123456"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
          <input
            type="text"
            value={editForm.vat_number || ''}
            onChange={(e) => setEditForm({...editForm, vat_number: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="GB123456789"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Registered Address</label>
          <div className="space-y-2">
            <input
              type="text"
              value={editForm.registered_address_line1 || ''}
              onChange={(e) => setEditForm({...editForm, registered_address_line1: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Address Line 1"
            />
            <input
              type="text"
              value={editForm.registered_address_line2 || ''}
              onChange={(e) => setEditForm({...editForm, registered_address_line2: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Address Line 2 (optional)"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={editForm.registered_city || ''}
                onChange={(e) => setEditForm({...editForm, registered_city: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="City"
              />
              <input
                type="text"
                value={editForm.registered_county || ''}
                onChange={(e) => setEditForm({...editForm, registered_county: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="County"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="text"
                value={editForm.registered_postcode || ''}
                onChange={(e) => setEditForm({...editForm, registered_postcode: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Postcode"
              />
              <input
                type="text"
                value={editForm.registered_country || 'United Kingdom'}
                onChange={(e) => setEditForm({...editForm, registered_country: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Country"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccountSettingsSection({ customer, editForm, setEditForm, editing }) {
  if (!editing) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InfoField label="Account Reference" value={customer.account_reference} />
        <InfoField label="Account Type" value={customer.account_type?.toUpperCase()} />
        <InfoField label="Credit Limit" value={formatCurrency(customer.credit_limit)} />
        <InfoField label="Payment Terms" value={customer.payment_terms || '30 Days Net'} />
        <InfoField 
          label="Account Status" 
          value={customer.account_on_stop ? '🚫 On Stop' : '✅ Approved'}
          className={customer.account_on_stop ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Account Reference</label>
        <input
          type="text"
          value={editForm.account_reference || ''}
          onChange={(e) => setEditForm({...editForm, account_reference: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          placeholder="e.g., CUST001"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
        <select
          value={editForm.account_type || ''}
          onChange={(e) => setEditForm({...editForm, account_type: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">Select type...</option>
          <option value="cash">Cash - Pay upfront</option>
          <option value="web">Web - Online orders</option>
          <option value="credit">Credit - Invoice terms</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Credit Limit (£)</label>
        <input
          type="number"
          step="0.01"
          value={editForm.credit_limit || ''}
          onChange={(e) => setEditForm({...editForm, credit_limit: parseFloat(e.target.value) || 0})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          placeholder="0.00"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
        <select
          value={editForm.payment_terms || ''}
          onChange={(e) => setEditForm({...editForm, payment_terms: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">Select terms...</option>
          <option value="Net 7">Net 7 Days</option>
          <option value="Net 14">Net 14 Days</option>
          <option value="Net 30">Net 30 Days</option>
          <option value="Net 60">Net 60 Days</option>
          <option value="Net 90">Net 90 Days</option>
          <option value="Cash on Delivery">Cash on Delivery</option>
          <option value="Payment in Advance">Payment in Advance</option>
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={editForm.account_on_stop || false}
            onChange={(e) => setEditForm({...editForm, account_on_stop: e.target.checked})}
            className="mr-2 h-4 w-4 text-red-600 rounded"
          />
          <span className="font-medium text-red-600">🚫 Place Account On Stop</span>
        </label>
        <p className="text-xs text-gray-500 ml-6 mt-1">Prevents new orders and invoicing</p>
      </div>
    </div>
  );
}

function InvoicingSection({ customer, editForm, setEditForm, editing, onEdit, onSave, onCancel }) {
  if (!editing) {
    return (
      <div>
        <div className="flex justify-end mb-3">
          <button onClick={onEdit} className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium">
            Edit
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <InfoField 
              label="Invoicing Address" 
              value={
                customer.invoicing_same_as_registered 
                  ? "Same as registered address"
                  : formatAddress(
                      customer.invoicing_address_line1,
                      customer.invoicing_address_line2,
                      customer.invoicing_city,
                      customer.invoicing_county,
                      customer.invoicing_postcode,
                      customer.invoicing_country
                    )
              }
              multiline
            />
          </div>
          <InfoField label="Default Type" value={customer.default_type?.toUpperCase()} />
          <div className="md:col-span-2">
            <InfoField label="Bank Details" value={customer.bank_details} multiline />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-3 gap-2">
        <button onClick={onSave} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded font-medium">
          Save
        </button>
        <button onClick={onCancel} className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium">
          Cancel
        </button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="flex items-center mb-3">
            <input
              type="checkbox"
              checked={editForm.invoicing_same_as_registered || false}
              onChange={(e) => setEditForm({...editForm, invoicing_same_as_registered: e.target.checked})}
              className="mr-2 h-4 w-4 text-blue-600 rounded"
            />
            <span className="font-medium text-gray-700">Use registered address for invoicing</span>
          </label>
        </div>

        {!editForm.invoicing_same_as_registered && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Invoicing Address</label>
            <div className="space-y-2">
              <input
                type="text"
                value={editForm.invoicing_address_line1 || ''}
                onChange={(e) => setEditForm({...editForm, invoicing_address_line1: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Address Line 1"
              />
              <input
                type="text"
                value={editForm.invoicing_address_line2 || ''}
                onChange={(e) => setEditForm({...editForm, invoicing_address_line2: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Address Line 2 (optional)"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={editForm.invoicing_city || ''}
                  onChange={(e) => setEditForm({...editForm, invoicing_city: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="City"
                />
                <input
                  type="text"
                  value={editForm.invoicing_county || ''}
                  onChange={(e) => setEditForm({...editForm, invoicing_county: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="County"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={editForm.invoicing_postcode || ''}
                  onChange={(e) => setEditForm({...editForm, invoicing_postcode: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Postcode"
                />
                <input
                  type="text"
                  value={editForm.invoicing_country || 'United Kingdom'}
                  onChange={(e) => setEditForm({...editForm, invoicing_country: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Country"
                />
              </div>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Default Type</label>
          <select
            value={editForm.default_type || ''}
            onChange={(e) => setEditForm({...editForm, default_type: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Select type...</option>
            <option value="service">Service</option>
            <option value="supply">Supply</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bank Details (for customer payments)</label>
          <textarea
            value={editForm.bank_details || ''}
            onChange={(e) => setEditForm({...editForm, bank_details: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            rows="4"
            placeholder="Bank name, sort code, account number, etc."
          />
        </div>
      </div>
    </div>
  );
}

function ContactsSection({ customer, editForm, setEditForm, editing, onEdit, onSave, onCancel }) {
  if (!editing) {
    return (
      <div>
        <div className="flex justify-end mb-3">
          <button onClick={onEdit} className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium">
            Edit
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoField label="Accounts Contact Name" value={customer.accounts_contact_name} />
          <InfoField label="Accounts Contact Phone" value={customer.accounts_contact_phone} />
          <InfoField label="Accounts Contact Email" value={customer.accounts_contact_email} />
          <div className="md:col-span-2">
            <InfoField 
              label="Additional Email Addresses (for invoice CC)" 
              value={customer.additional_emails} 
              multiline 
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-3 gap-2">
        <button onClick={onSave} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded font-medium">
          Save
        </button>
        <button onClick={onCancel} className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium">
          Cancel
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
          <input
            type="text"
            value={editForm.accounts_contact_name || ''}
            onChange={(e) => setEditForm({...editForm, accounts_contact_name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="John Smith"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
          <input
            type="tel"
            value={editForm.accounts_contact_phone || ''}
            onChange={(e) => setEditForm({...editForm, accounts_contact_phone: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="01234 567890"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
          <input
            type="email"
            value={editForm.accounts_contact_email || ''}
            onChange={(e) => setEditForm({...editForm, accounts_contact_email: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="accounts@company.com"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Additional Email Addresses (comma-separated)
          </label>
          <input
            type="text"
            value={editForm.additional_emails || ''}
            onChange={(e) => setEditForm({...editForm, additional_emails: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="director@company.com, finance@company.com"
          />
          <p className="text-xs text-gray-500 mt-1">For CC on invoices and remittance advice</p>
        </div>
      </div>
    </div>
  );
}

function CISSection({ customer, editForm, setEditForm, editing, onEdit, onSave, onCancel }) {
  if (!editing) {
    return (
      <div>
        <div className="flex justify-end mb-3">
          <button onClick={onEdit} className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium">
            Edit
          </button>
        </div>
        
        {!customer.is_cis_registered ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg mb-2">Not registered for CIS</p>
            <p className="text-sm">Click Edit to enable Construction Industry Scheme registration</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoField label="CIS Registered" value="✅ Yes" className="text-green-600 font-semibold" />
            <InfoField label="Registered CIS Name" value={customer.cis_registered_name} />
            <InfoField label="UTR (Unique Taxpayer Reference)" value={customer.cis_utr} />
            <InfoField label="CIS Rate" value={customer.cis_rate ? `${customer.cis_rate}%` : null} />
            <InfoField label="CIS Status" value={customer.cis_status} />
            <InfoField label="Verification Number" value={customer.cis_verification_number} />
            <InfoField label="Default Products Tax" value={customer.default_products_tax ? `${customer.default_products_tax}%` : null} />
            <InfoField label="Default Services Tax" value={customer.default_services_tax ? `${customer.default_services_tax}%` : null} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-3 gap-2">
        <button onClick={onSave} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded font-medium">
          Save
        </button>
        <button onClick={onCancel} className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium">
          Cancel
        </button>
      </div>
      
      <div className="space-y-4">
        <div>
          <label className="flex items-center mb-3">
            <input
              type="checkbox"
              checked={editForm.is_cis_registered || false}
              onChange={(e) => setEditForm({...editForm, is_cis_registered: e.target.checked})}
              className="mr-2 h-4 w-4 text-orange-600 rounded"
            />
            <span className="font-medium text-gray-700">CIS Registered (Construction Industry Scheme)</span>
          </label>
          <p className="text-xs text-gray-500 ml-6">Subcontractor subject to CIS deductions</p>
        </div>

        {editForm.is_cis_registered && (
          <div className="ml-6 space-y-4 pt-4 border-t">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registered CIS Name</label>
                <input
                  type="text"
                  value={editForm.cis_registered_name || ''}
                  onChange={(e) => setEditForm({...editForm, cis_registered_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="Company trading name for CIS"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">UTR (Unique Taxpayer Reference)</label>
                <input
                  type="text"
                  value={editForm.cis_utr || ''}
                  onChange={(e) => setEditForm({...editForm, cis_utr: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="1234567890"
                  maxLength="10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CIS Status & Rate</label>
                <select
                  value={editForm.cis_status || 'standard'}
                  onChange={(e) => {
                    const status = e.target.value;
                    let rate = 20;
                    if (status === 'higher') rate = 30;
                    if (status === 'gross') rate = 0;
                    setEditForm({...editForm, cis_status: status, cis_rate: rate});
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="standard">Standard Rate (20%)</option>
                  <option value="higher">Higher Rate (30%)</option>
                  <option value="gross">Gross Payment Status (0%)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CIS Verification Number</label>
                <input
                  type="text"
                  value={editForm.cis_verification_number || ''}
                  onChange={(e) => setEditForm({...editForm, cis_verification_number: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="12345678"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Products Tax Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.default_products_tax || ''}
                  onChange={(e) => setEditForm({...editForm, default_products_tax: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="20.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Services Tax Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.default_services_tax || ''}
                  onChange={(e) => setEditForm({...editForm, default_services_tax: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="20.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CIS Verified Date</label>
                <input
                  type="date"
                  value={editForm.cis_verified_date || ''}
                  onChange={(e) => setEditForm({...editForm, cis_verified_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PricingSection({ customer, editForm, setEditForm, editing, onEdit, onSave, onCancel }) {
  if (!editing) {
    return (
      <div>
        <div className="flex justify-end mb-3">
          <button onClick={onEdit} className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium">
            Edit
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoField label="Trade Customer" value={customer.is_trade_customer ? '✅ Yes' : '❌ No'} />
          <InfoField label="Discount Percentage" value={customer.discount_percentage ? `${customer.discount_percentage}%` : '0%'} />
          <InfoField label="Reverse VAT" value={customer.apply_reverse_vat ? '✅ Applies' : '❌ No'} />
          <div className="md:col-span-2">
            <InfoField label="Special Pricing Notes" value={customer.special_pricing_notes} multiline />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-3 gap-2">
        <button onClick={onSave} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded font-medium">
          Save
        </button>
        <button onClick={onCancel} className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-black text-sm rounded font-medium">
          Cancel
        </button>
      </div>
      <div className="space-y-4">
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={editForm.is_trade_customer || false}
              onChange={(e) => setEditForm({...editForm, is_trade_customer: e.target.checked})}
              className="mr-2 h-4 w-4 text-yellow-600 rounded"
            />
            <span className="font-medium text-gray-700">Trade Customer</span>
          </label>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Discount Percentage (%)</label>
          <input
            type="number"
            step="0.01"
            value={editForm.discount_percentage || ''}
            onChange={(e) => setEditForm({...editForm, discount_percentage: parseFloat(e.target.value) || 0})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="0.00"
          />
        </div>
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={editForm.apply_reverse_vat || false}
              onChange={(e) => setEditForm({...editForm, apply_reverse_vat: e.target.checked})}
              className="mr-2 h-4 w-4 text-blue-600 rounded"
            />
            <span className="font-medium text-gray-700">Apply Reverse VAT (Domestic Reverse Charge)</span>
          </label>
          <p className="text-xs text-gray-500 ml-6 mt-1">For building/construction services where customer accounts for VAT</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Special Pricing Notes</label>
          <textarea
            value={editForm.special_pricing_notes || ''}
            onChange={(e) => setEditForm({...editForm, special_pricing_notes: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            rows="3"
            placeholder="Any special pricing agreements or notes..."
          />
        </div>
      </div>
    </div>
  );
}

function CurrentBalanceSection({ customer }) {
  const availableCredit = (customer.credit_limit || 0) - (customer.total_outstanding || 0);
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="text-sm text-gray-600 mb-1">Outstanding</div>
        <div className="text-2xl font-bold text-orange-600">
          {formatCurrency(customer.total_outstanding)}
        </div>
      </div>
      
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="text-sm text-gray-600 mb-1">Overdue</div>
        <div className={`text-2xl font-bold ${
          (customer.total_overdue || 0) > 0 ? 'text-red-600' : 'text-green-600'
        }`}>
          {formatCurrency(customer.total_overdue)}
        </div>
      </div>
      
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="text-sm text-gray-600 mb-1">Available Credit</div>
        <div className={`text-2xl font-bold ${
          availableCredit >= 0 ? 'text-green-600' : 'text-red-600'
        }`}>
          {formatCurrency(availableCredit)}
        </div>
      </div>
      
      {customer.last_payment_date && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Last Payment</div>
          <div className="text-lg font-semibold text-blue-800">
            {new Date(customer.last_payment_date).toLocaleDateString('en-GB')}
          </div>
        </div>
      )}
      
      {customer.average_days_to_pay && (
        <div className={`border rounded-lg p-4 ${
          customer.average_days_to_pay <= 30 ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'
        }`}>
          <div className="text-sm text-gray-600 mb-1">Avg Days to Pay</div>
          <div className={`text-2xl font-bold ${
            customer.average_days_to_pay <= 30 ? 'text-green-600' : 'text-orange-600'
          }`}>
            {customer.average_days_to_pay} days
          </div>
        </div>
      )}
    </div>
  );
}

function XeroSection({ customer }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Sync Status:</span>
          <XeroSyncStatus status={customer.xero_sync_status} />
        </div>
        
        {customer.xero_customer_id && (
          <>
            <InfoField label="Xero ID" value={customer.xero_customer_id.slice(0, 20) + '...'} />
            <InfoField label="Account Code" value={customer.xero_account_code} />
            {customer.xero_synced_at && (
              <InfoField 
                label="Last Sync" 
                value={new Date(customer.xero_synced_at).toLocaleString('en-GB')} 
              />
            )}
          </>
        )}
      </div>
      
      <div className="flex gap-3 pt-4 border-t">
        <button className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition">
          🔄 Sync to Xero
        </button>
        {customer.xero_customer_id && (
          <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition">
            👁️ View in Xero
          </button>
        )}
      </div>
    </div>
  );
}

// Helper Components
function InfoField({ label, value, className = '', multiline = false }) {
  return (
    <div>
      <div className="text-sm font-medium text-gray-600 mb-1">{label}</div>
      <div className={`text-gray-900 ${multiline ? 'whitespace-pre-line' : ''} ${className}`}>
        {value || <span className="text-gray-400 italic">Not set</span>}
      </div>
    </div>
  );
}

function XeroSyncStatus({ status }) {
  const statusConfig = {
    not_synced: { color: 'gray', icon: '⚪', text: 'Not Synced' },
    synced: { color: 'green', icon: '✅', text: 'Synced' },
    failed: { color: 'red', icon: '❌', text: 'Failed' },
    out_of_sync: { color: 'orange', icon: '⚠️', text: 'Out of Sync' }
  };
  
  const config = statusConfig[status] || statusConfig.not_synced;
  
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${config.color}-100 text-${config.color}-800 border border-${config.color}-300`}>
      {config.icon} {config.text}
    </span>
  );
}

// Helper Functions
function formatCurrency(amount) {
  if (amount === null || amount === undefined) return '£0.00';
  return `£${parseFloat(amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatAddress(...parts) {
  return parts.filter(Boolean).join('\n') || null;
}

export default CustomerFinanceTab;
